import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { KeyRound, Loader2, CheckCircle, AlertTriangle } from 'lucide-react'
import { api } from '../api/client'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    try {
      await api.post('/auth/reset-password', { token, new_password: password })
      setSuccess(true)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-background py-12 px-4">
        <div className="max-w-md w-full text-center space-y-4 bg-theme-surface p-8 rounded-lg shadow border border-theme-border">
          <AlertTriangle className="mx-auto text-yellow-500" size={48} />
          <h2 className="text-lg font-semibold text-theme-text-primary">Invalid Reset Link</h2>
          <p className="text-theme-text-secondary text-sm">This link is missing a reset token.</p>
          <Link to="/forgot-password" className="text-theme-primary hover:text-theme-primary-hover text-sm font-medium">
            Request a new reset link
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-background py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-theme-text-primary">Forge-Quote</h1>
          <h2 className="mt-2 text-xl text-theme-text-secondary">Set new password</h2>
        </div>

        <div className="bg-theme-surface p-8 rounded-lg shadow border border-theme-border">
          {success ? (
            <div className="text-center space-y-4">
              <CheckCircle className="mx-auto text-green-500" size={48} />
              <h3 className="text-lg font-semibold text-theme-text-primary">Password reset!</h3>
              <p className="text-theme-text-secondary text-sm">
                Your password has been updated. You can now sign in.
              </p>
              <Link
                to="/login"
                className="inline-block px-6 py-2 bg-theme-primary text-white rounded-md hover:bg-theme-primary-hover text-sm font-medium"
              >
                Sign In
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-theme-text-primary">New Password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="mt-1 block w-full border border-theme-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-theme-primary focus:border-theme-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-theme-text-primary">Confirm Password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className="mt-1 block w-full border border-theme-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-theme-primary focus:border-theme-primary"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center gap-2 py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white bg-theme-primary hover:bg-theme-primary-hover disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      <KeyRound size={20} />
                      Reset Password
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
